import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const navItems = [
  { section: 'Overview', items: [
    { to: '/', icon: '📊', label: 'Dashboard', exact: true, roles: ['ADM', 'MGR'] },
  ]},
  { section: 'Operations', items: [
    { to: '/apartments', icon: '🏢', label: 'Orders (Grouped)' },
    { to: '/customers', icon: '👤', label: 'Customer Details' },
    { to: '/orders', icon: '📦', label: 'Orders (List)' },
    { to: '/counter-sales', icon: '🏪', label: 'Counter Sales', roles: ['ADM','MGR'] },
  ]},
  { section: 'Catalog', items: [
    { to: '/products', icon: '🛒', label: 'Products', roles: ['ADM','MGR'] },
    { to: '/inventory', icon: '📝', label: 'Inventory / Wastage', roles: ['ADM','MGR'] },
  ]},
  { section: 'Team', items: [
    { to: '/staff', icon: '👥', label: 'Staff', roles: ['ADM','MGR'] },
    { to: '/vehicles', icon: '🚚', label: 'Vehicles', roles: ['ADM','MGR'] },
  ]},
  { section: 'HR', items: [
    { to: '/attendance', icon: '🕐', label: 'Attendance', roles: ['ADM','MGR'] },
    { to: '/salary', icon: '💰', label: 'Salary & Payroll', roles: ['ADM','MGR'] },
  ]},
  { section: 'Finance', items: [
    { to: '/finance', icon: '📊', label: 'Finance / P&L', roles: ['ADM','MGR'] },
  ]},
];

const roleLabels = { ADM: 'Administrator', MGR: 'Manager', STF: 'Staff' };

const Layout = () => {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile nav)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on Escape key
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const { data } = await api.get('/analytics/notifications?limit=1');
        setUnread(data.data.unreadCount || 0);
      } catch {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, []);

  const pageTitle = () => {
    const path = location.pathname;
    if (path === '/') return { title: 'Dashboard', sub: 'Welcome back, ' + (user?.fullName || user?.username) };
    if (path.startsWith('/apartments')) return { title: 'Apartments', sub: 'Manage delivery zones' };
    if (path.startsWith('/customers')) return { title: 'Customer Details', sub: 'Browse customers by apartment' };
    if (path.startsWith('/counter-sales')) return { title: 'Counter Sales', sub: 'Walk-in & live counter transactions' };
    if (path.startsWith('/orders')) return { title: 'Orders', sub: 'Track and manage orders' };
    if (path.startsWith('/products')) return { title: 'Products', sub: 'Inventory management' };
    if (path.startsWith('/inventory')) return { title: 'Inventory / Wastage', sub: 'Manage product stock' };
    if (path.startsWith('/staff')) return { title: 'Staff', sub: 'Team management' };
    if (path.startsWith('/vehicles')) return { title: 'Vehicles', sub: 'Fleet management' };
    if (path.startsWith('/attendance')) return { title: 'Attendance', sub: 'Employee attendance tracking' };
    if (path.startsWith('/salary')) return { title: 'Salary & Payroll', sub: 'Salary structures and payroll management' };
    if (path.startsWith('/finance')) return { title: 'Finance / P&L', sub: 'Revenue, expenses, and profit/loss tracking' };
    return { title: 'ERP System', sub: '' };
  };

  const { title, sub } = pageTitle();

  return (
    <div className="app-layout">
      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/v.png" alt="V2V Superfresh" className="logo-icon" />
            <div>
              <div className="logo-text">V2V Superfresh</div>
              <div className="logo-sub">Delivery Management</div>
            </div>
          </div>
          {/* Close button inside sidebar (mobile) */}
          <button
            className="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >✕</button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(section => {
            const visible = section.items.filter(item =>
              !item.roles || item.roles.some(r => hasRole(r))
            );
            if (visible.length === 0) return null;
            return (
              <div key={section.section} className="nav-section">
                <div className="nav-section-title">{section.section}</div>
                {visible.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {item.label}
                    {item.label === 'Orders' && unread > 0 && (
                      <span className="nav-badge">{unread}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-card">
            <div className="user-avatar">
              {(user?.fullName || user?.username || 'U')[0].toUpperCase()}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.fullName || user?.username}</div>
              <div className="user-role">{user?.role} · {roleLabels[user?.role]}</div>
            </div>
            <button className="logout-btn" onClick={() => { logout(); window.location.href = '/erp'; }} title="Logout">⏻</button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        <div className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Hamburger — visible only on mobile */}
            <button
              className="hamburger-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <span /><span /><span />
            </button>
            <div>
              <div className="topbar-title">{title}</div>
              {sub && <div className="topbar-sub">{sub}</div>}
            </div>
          </div>
          <div className="topbar-actions">
            <button
              className="btn btn-secondary btn-sm notif-btn"
              style={{ fontSize: '18px', padding: '8px 10px' }}
            >
              🔔 {unread > 0 && <span className="notif-dot"></span>}
            </button>
            <div className={`badge badge-${user?.role}`}>{user?.role}</div>
          </div>
        </div>
        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
